import { useMutation } from '@tanstack/react-query';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { useToast } from '../ui/Toast.js';
import { inlineErrorMessage } from '../../lib/error-handling.js';

/** The record a document is removed from, as the user names it. */
export type DocumentRecordNoun = 'expense' | 'asset' | 'bank line' | 'journal' | 'time entry';

interface RemoveDocumentDialogProps {
  /** The document to remove; the dialog is open while this is set. */
  document: { id: string; filename: string } | null;
  record: DocumentRecordNoun;
  /** The record's own DELETE, which unlinks the document and leaves the file with the practice. */
  remove: (documentId: string) => Promise<unknown>;
  /** Refresh the caches that list the record's documents. */
  onRemoved: () => void;
  onClose: () => void;
}

/**
 * Confirms removing a document from one record. Removal only unlinks it, so the
 * dialog is not danger-styled and never says the action cannot be undone. The
 * copy does not mention that the file is kept: Bill has no surface where a user
 * could find an unlinked file again, so saying so would only confuse. It owns
 * the mutation and its toasts so every documents panel reads the same.
 */
export function RemoveDocumentDialog({ document, record, remove, onRemoved, onClose }: RemoveDocumentDialogProps) {
  const { addToast } = useToast();
  const open = document !== null;

  const mutation = useMutation({
    mutationFn: (documentId: string) => remove(documentId),
    onSuccess: () => {
      onRemoved();
      addToast({ variant: 'success', title: 'Document removed' });
      onClose();
    },
    onError: (err) => {
      addToast({
        variant: 'error',
        title: "Couldn't remove document",
        description: inlineErrorMessage(err, 'Try again.'),
      });
      onClose();
    },
  });

  return (
    <ConfirmDialog
      open={open}
      title="Remove document"
      confirmLabel="Remove"
      variant="primary"
      loading={mutation.isPending}
      onConfirm={() => {
        if (document) mutation.mutate(document.id);
      }}
      onCancel={onClose}
    >
      {/* TODO: once Bill shows a document attached to several records, or lists
          unlinked documents, separate Remove from Delete. Removing a document's
          last live link should read as Delete and archive the document itself;
          removing one of several links stays Remove and says the file stays on
          the other records. */}
      <p>
        Remove <span className="break-words font-medium text-secondary">{document?.filename}</span> from this {record}?
      </p>
    </ConfirmDialog>
  );
}
